/**
 * Workspace Generator - Creates a fully configured workspace
 * with seed data based on business analysis.
 * Browser-compatible version with enhanced Linear/HubSpot-style data.
 *
 * References:
 * - Linear: Task workflow with triage/backlog/in_progress/in_review/done
 * - HubSpot: CRM pipeline with lead/qualified/proposal/negotiation/customer
 * - Notion: Notes with hierarchy and rich organization
 * - Stripe: KPI-focused insights
 */

import type { Task, Contact, Note, SetupResult, TeamMember } from './types';
import type { BusinessAnalysis } from './business-analyzer';
import { createWorkspace } from './services/workspace.service';
import { createTask } from './services/tasks.service';
import { createContact } from './services/crm.service';
import { createNote } from './services/notes.service';
import { generateWorkspaceInsights } from './services/insights.service';
import { generateWorkspaceSuggestions } from './services/assistant.service';
import { generateDemoTeamMembers } from './services/team.service';

export function generateWorkspaceFromAnalysis(analysis: BusinessAnalysis): SetupResult {
  const workspace = createWorkspace({
    name: analysis.businessName,
    businessType: analysis.businessType,
    description: `${analysis.industry} ${analysis.businessType}`,
  });

  // Generate team members first so we can assign them to tasks/contacts
  const teamMembers = generateDemoTeamMembers(workspace.id);

  const generatedTasks = generateSeedTasks(workspace.id, analysis, teamMembers);
  const generatedContacts = generateSeedContacts(workspace.id, analysis, teamMembers);
  const generatedNotes = generateSeedNotes(workspace.id, analysis);
  const insights = generateWorkspaceInsights(workspace.id);
  const suggestions = generateWorkspaceSuggestions(workspace.id);

  return {
    workspace,
    generatedTasks,
    generatedContacts,
    generatedNotes,
    insights,
    suggestions,
    teamMembers,
  };
}

function generateSeedTasks(workspaceId: string, analysis: BusinessAnalysis, teamMembers: TeamMember[]): Task[] {
  const tasks: Task[] = [];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  const nextMonth = new Date();
  nextMonth.setDate(nextMonth.getDate() + 30);

  // Get team members by role for assignment
  const teamLead = teamMembers.find((m) => m.role === 'team_lead');
  const developers = teamMembers.filter((m) => m.role === 'developer');
  const planner = teamMembers.find((m) => m.role === 'planner');
  const qa = teamMembers.find((m) => m.role === 'qa');

  // Triage tasks (new, unprocessed)
  tasks.push(
    createTask({
      workspaceId,
      title: 'Review incoming feature requests',
      description: 'Go through the new feature requests and prioritize them for the next sprint.',
      status: 'triage',
      priority: 'high',
      tags: ['planning', 'features'],
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'Investigate reported bug in checkout',
      description: 'Users reported intermittent issues with the checkout process.',
      status: 'triage',
      priority: 'urgent',
      tags: ['bug', 'checkout'],
    })
  );

  // Backlog tasks (accepted, waiting to start)
  tasks.push(
    createTask({
      workspaceId,
      title: 'Set up automated testing pipeline',
      description: 'Configure CI/CD pipeline with automated tests for deployment validation.',
      status: 'backlog',
      priority: 'medium',
      assigneeId: qa?.id,
      dueDate: nextMonth,
      tags: ['devops', 'testing', 'automation'],
      estimate: 8,
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'Design new dashboard widgets',
      description: 'Create mockups for the new analytics dashboard widgets requested by customers.',
      status: 'backlog',
      priority: 'medium',
      assigneeId: planner?.id,
      dueDate: nextWeek,
      tags: ['design', 'dashboard'],
      estimate: 5,
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'Implement dark mode support',
      description: 'Add theme switching capability with dark mode option.',
      status: 'backlog',
      priority: 'low',
      assigneeId: developers[0]?.id,
      tags: ['frontend', 'ui'],
      estimate: 13,
    })
  );

  // In Progress tasks
  tasks.push(
    createTask({
      workspaceId,
      title: 'Build CRM integration module',
      description: 'Develop the integration module for connecting with external CRM systems.',
      status: 'in_progress',
      priority: 'high',
      assigneeId: developers[0]?.id,
      dueDate: nextWeek,
      tags: ['backend', 'integration', 'crm'],
      estimate: 8,
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'Optimize database queries',
      description: 'Improve performance of slow queries identified in monitoring.',
      status: 'in_progress',
      priority: 'high',
      assigneeId: developers[1]?.id,
      dueDate: tomorrow,
      tags: ['performance', 'database'],
      estimate: 5,
    })
  );

  // In Review tasks
  tasks.push(
    createTask({
      workspaceId,
      title: 'User authentication redesign',
      description: 'New authentication flow with social login support - ready for review.',
      status: 'in_review',
      priority: 'high',
      assigneeId: developers[0]?.id,
      tags: ['auth', 'security'],
      estimate: 13,
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'API documentation update',
      description: 'Updated API docs with new endpoints and examples - pending approval.',
      status: 'in_review',
      priority: 'medium',
      assigneeId: planner?.id,
      tags: ['docs', 'api'],
      estimate: 3,
    })
  );

  // Done tasks (completed)
  tasks.push(
    createTask({
      workspaceId,
      title: 'Set up project infrastructure',
      description: 'Initial project setup with development environment configuration.',
      status: 'done',
      priority: 'high',
      assigneeId: teamLead?.id,
      tags: ['setup', 'infrastructure'],
    })
  );

  tasks.push(
    createTask({
      workspaceId,
      title: 'Create initial database schema',
      description: 'Designed and implemented the core database schema.',
      status: 'done',
      priority: 'high',
      assigneeId: developers[1]?.id,
      tags: ['database', 'schema'],
    })
  );

  // Business-specific tasks
  if (analysis.goals.includes('신규 고객 확보')) {
    tasks.push(
      createTask({
        workspaceId,
        title: 'Develop lead scoring algorithm',
        description: 'Implement ML-based lead scoring to prioritize sales efforts.',
        status: 'backlog',
        priority: 'high',
        assigneeId: developers[0]?.id,
        dueDate: nextWeek,
        tags: ['ml', 'sales', 'leads'],
        estimate: 13,
      })
    );
  }

  if (analysis.goals.includes('업무 효율화')) {
    tasks.push(
      createTask({
        workspaceId,
        title: 'Automate weekly reporting',
        description: 'Create automated reports for team performance metrics.',
        status: 'backlog',
        priority: 'medium',
        assigneeId: planner?.id,
        dueDate: nextMonth,
        tags: ['automation', 'reporting'],
        estimate: 5,
      })
    );
  }

  if (analysis.goals.includes('매출 증대')) {
    tasks.push(
      createTask({
        workspaceId,
        title: 'Implement revenue analytics dashboard',
        description: 'Build real-time revenue tracking with forecasting.',
        status: 'in_progress',
        priority: 'high',
        assigneeId: developers[0]?.id,
        dueDate: nextWeek,
        tags: ['analytics', 'revenue'],
        estimate: 8,
      })
    );
  }

  return tasks;
}

function generateSeedContacts(workspaceId: string, _analysis: BusinessAnalysis, teamMembers: TeamMember[]): Contact[] {
  const contacts: Contact[] = [];

  // Get sales-related team members for owner assignment
  const teamLead = teamMembers.find((m) => m.role === 'team_lead');
  const planner = teamMembers.find((m) => m.role === 'planner');

  // Leads
  contacts.push(
    createContact({
      workspaceId,
      name: 'Alex Thompson',
      company: 'TechStartup Inc.',
      role: 'CTO',
      email: 'alex.t@techstartup.com',
      phone: '+1-555-0101',
      stage: 'lead',
      notes: 'Interested in our enterprise plan. Met at the SaaS conference.',
      tags: ['conference', 'enterprise'],
      source: 'Conference',
      dealValue: 50000,
      ownerId: teamLead?.id,
    })
  );

  contacts.push(
    createContact({
      workspaceId,
      name: '김서연',
      company: 'DataFlow Solutions',
      role: 'Product Manager',
      email: 'seoyeon.kim@dataflow.io',
      phone: '010-1234-5678',
      stage: 'lead',
      notes: 'Downloaded whitepaper, requested demo.',
      tags: ['whitepaper', 'demo-request'],
      source: 'Website',
      dealValue: 30000,
      ownerId: planner?.id,
    })
  );

  contacts.push(
    createContact({
      workspaceId,
      name: 'Marcus Chen',
      company: 'InnovateTech',
      role: 'Engineering Lead',
      email: 'm.chen@innovatetech.com',
      stage: 'lead',
      notes: 'Referred by existing customer.',
      tags: ['referral'],
      source: 'Referral',
      dealValue: 25000,
    })
  );

  // Qualified leads
  contacts.push(
    createContact({
      workspaceId,
      name: 'Sarah Johnson',
      company: 'Global Dynamics',
      role: 'VP of Operations',
      email: 's.johnson@globaldyn.com',
      phone: '+1-555-0202',
      stage: 'qualified',
      notes: 'Had discovery call. Good budget fit, decision maker.',
      tags: ['qualified', 'high-value'],
      source: 'LinkedIn',
      dealValue: 120000,
      ownerId: teamLead?.id,
    })
  );

  contacts.push(
    createContact({
      workspaceId,
      name: '박준혁',
      company: 'CloudNine Systems',
      role: 'IT Director',
      email: 'junhyuk.park@cloudnine.co.kr',
      phone: '02-1234-5678',
      stage: 'qualified',
      notes: 'Completed demo. Evaluating vs competitors.',
      tags: ['demo-completed', 'evaluation'],
      source: 'Webinar',
      dealValue: 80000,
      ownerId: planner?.id,
    })
  );

  // Proposal sent
  contacts.push(
    createContact({
      workspaceId,
      name: 'Emily Williams',
      company: 'Nexus Corp',
      role: 'CEO',
      email: 'emily.w@nexuscorp.com',
      phone: '+1-555-0303',
      stage: 'proposal',
      notes: 'Proposal sent 3 days ago. Following up this week.',
      tags: ['proposal-sent', 'follow-up'],
      source: 'Cold Outreach',
      dealValue: 200000,
      ownerId: teamLead?.id,
    })
  );

  // Negotiation
  contacts.push(
    createContact({
      workspaceId,
      name: '이민준',
      company: 'FutureTech Korea',
      role: 'Managing Director',
      email: 'minjun.lee@futuretech.kr',
      phone: '010-9876-5432',
      stage: 'negotiation',
      notes: 'Negotiating contract terms. Expected close this month.',
      tags: ['negotiation', 'high-priority'],
      source: 'Partner Referral',
      dealValue: 350000,
      ownerId: teamLead?.id,
    })
  );

  // Customers
  contacts.push(
    createContact({
      workspaceId,
      name: 'David Miller',
      company: 'Prime Solutions',
      role: 'Head of Engineering',
      email: 'd.miller@primesolutions.com',
      phone: '+1-555-0404',
      stage: 'customer',
      notes: 'VIP customer. Been with us for 2 years. Upsell opportunity.',
      tags: ['vip', 'upsell', 'customer'],
      source: 'Trade Show',
      dealValue: 150000,
      ownerId: planner?.id,
    })
  );

  contacts.push(
    createContact({
      workspaceId,
      name: '최지우',
      company: 'SmartOps Co.',
      role: 'Operations Manager',
      email: 'jiwoo.choi@smartops.kr',
      phone: '02-5555-1234',
      stage: 'customer',
      notes: 'Happy customer. Provided case study testimonial.',
      tags: ['testimonial', 'case-study', 'customer'],
      source: 'Website',
      dealValue: 45000,
    })
  );

  // Churned (for realism)
  contacts.push(
    createContact({
      workspaceId,
      name: 'James Wilson',
      company: 'OldTech Inc.',
      role: 'IT Manager',
      email: 'j.wilson@oldtech.com',
      stage: 'churned',
      notes: 'Churned due to budget cuts. Keep for future re-engagement.',
      tags: ['churned', 'budget'],
      source: 'Trade Show',
      dealValue: 0,
    })
  );

  return contacts;
}

function generateSeedNotes(workspaceId: string, analysis: BusinessAnalysis): Note[] {
  const notes: Note[] = [];

  // Business Overview (Notion-style with icon)
  notes.push(
    createNote({
      workspaceId,
      title: 'Business Overview',
      content: `# ${analysis.businessName}

## About
${analysis.businessType} business in the ${analysis.industry} industry.

## Services We Offer
${analysis.services.map((s) => `- ${s}`).join('\n')}

## Target Customers
${analysis.targetCustomers.map((c) => `- ${c}`).join('\n')}

## Strategic Goals
${analysis.goals.map((g) => `- ${g}`).join('\n')}

## Current Challenges
${analysis.challenges.map((c) => `- ${c}`).join('\n')}

---
*Auto-generated overview. Update with actual business details.*`,
      tags: ['overview', 'strategy'],
      icon: '🏢',
      isPinned: true,
    })
  );

  // Quick Start Guide
  notes.push(
    createNote({
      workspaceId,
      title: 'Getting Started Guide',
      content: `# Quick Start Guide

## First Day Checklist
- [ ] Review and update business information
- [ ] Add your first real contact to CRM
- [ ] Create your first task in the triage queue

## This Week's Goals
- Move items from Triage to Backlog
- Qualify at least 2 new leads
- Document one key process

## Pro Tips
- **Linear-style workflow**: Triage → Backlog → In Progress → In Review → Done
- **HubSpot-style CRM**: Lead → Qualified → Proposal → Negotiation → Customer
- Review the dashboard daily to track KPIs

## Keyboard Shortcuts
- \`Ctrl + K\` - Quick search
- \`C\` - Create new item
- \`→\` - Move task forward

## Need Help?
Ask the AI Assistant for guidance on any feature!`,
      tags: ['guide', 'onboarding'],
      icon: '🚀',
      isPinned: true,
    })
  );

  // Team Playbook
  notes.push(
    createNote({
      workspaceId,
      title: 'Team Playbook',
      content: `# Team Playbook

## Roles & Responsibilities

### Team Lead
- Sprint planning and prioritization
- Blockers resolution
- Stakeholder communication

### Developer
- Feature development
- Code reviews
- Technical documentation

### Planner (PM)
- Requirements gathering
- Product roadmap
- Customer feedback analysis

### QA
- Test planning and execution
- Quality metrics tracking
- Release verification

## Daily Standup Format
1. What I did yesterday
2. What I'm doing today
3. Any blockers?

## Sprint Cycle
- Sprint duration: 2 weeks
- Planning: Monday morning
- Review: Friday afternoon
- Retro: Friday EOD`,
      tags: ['team', 'process'],
      icon: '📋',
    })
  );

  // Meeting Notes Template
  notes.push(
    createNote({
      workspaceId,
      title: 'Meeting Notes Template',
      content: `# Meeting Notes: [Topic]

**Date:** [Date]
**Attendees:** [Names]
**Duration:** [Time]

## Agenda
1.
2.
3.

## Discussion Points
-

## Decisions Made
-

## Action Items
| Task | Owner | Due Date |
|------|-------|----------|
|      |       |          |

## Next Steps
-

---
*Template for consistent meeting documentation.*`,
      tags: ['template', 'meetings'],
      icon: '📝',
    })
  );

  // Sales Process Document
  notes.push(
    createNote({
      workspaceId,
      title: 'Sales Process Overview',
      content: `# Sales Process

## Pipeline Stages

### 1. Lead
- Initial contact or inquiry
- No qualification yet
- Goal: Schedule discovery call

### 2. Qualified
- Budget confirmed
- Timeline identified
- Decision maker engaged
- Goal: Book demo

### 3. Proposal
- Demo completed
- Proposal sent
- Goal: Handle objections, negotiate

### 4. Negotiation
- Terms being discussed
- Contract in review
- Goal: Close the deal

### 5. Customer
- Deal closed!
- Onboarding begins
- Goal: Ensure success, upsell

## Key Metrics
- Lead to Qualified: 30%
- Qualified to Proposal: 50%
- Proposal to Close: 40%
- Average Deal Size: ₩100,000,000

## Follow-up Cadence
- Lead: Contact within 24 hours
- Qualified: Weekly touchpoint
- Proposal: Every 2-3 days
- Negotiation: Daily if needed`,
      tags: ['sales', 'process', 'crm'],
      icon: '💼',
    })
  );

  return notes;
}
